import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletManager, type WalletInfo } from '../../utils/walletManager';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../hooks/useConfirm';

const WalletStatusBar: React.FC = () => {
  const [walletInfo, setWalletInfo] = useState<WalletInfo | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // 妫€鏌ュ垵濮嬭繛鎺ョ姸鎬?
    checkConnectionStatus();

    // 鐩戝惉閽卞寘鐘舵€佸彉鍖栦簨浠?
    const handleWalletStatusChange = (event: CustomEvent) => {
      console.log('閽卞寘鐘舵€佸彉鍖栦簨浠?', event.detail);
      const { isConnected, walletInfo } = event.detail;
      setIsConnected(isConnected);
      setWalletInfo(walletInfo);
    };

    window.addEventListener('walletStatusChanged', handleWalletStatusChange as EventListener);

    // 璁剧疆瀹氭湡妫€鏌ワ紙鍑忓皯棰戠巼锛?
    const interval = setInterval(checkConnectionStatus, 10000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('walletStatusChanged', handleWalletStatusChange as EventListener);
    };
  }, []);

  const checkConnectionStatus = async () => {
    const connected = await walletManager.isConnected();
    const info = walletManager.getWalletInfo();
    
    setIsConnected(connected);
    setWalletInfo(info);
    
    if (connected) {
      walletManager.refreshActivity();
    }
  };

  const handleDisconnect = () => {
    walletManager.disconnect();
    setIsConnected(false);
    setWalletInfo(null);
  };

  const handleReconnect = async () => {
    const result = await walletManager.connectWallet();
    if (result.success) {
      setIsConnected(true);
      setWalletInfo(result.walletInfo || null);
      return;
    }
    // 如果无法直接连接，引导到专门的钱包连接页面
    navigate('/wallet-connect');
  };

  if (!isConnected || !walletInfo) {
    return (
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 px-4 text-center text-sm">
        <div className="flex items-center justify-center gap-2">
          <i className="fas fa-wallet"></i>
          <span>钱包未连接</span>
          <button
            onClick={handleReconnect}
            className="ml-2 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
          >
            重新连接钱包
          </button>
        </div>
      </div>
    );
  }

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'metamask': return '🦊';
      case 'coinbase': return '💼';
      case 'trust': return '🔒';
      case 'binance': return '🟡';
      case 'imtoken': return '💎';
      case 'bitget': return '🟢';
      case 'okx': return '🆗';
      case 'tokenpocket': return '👛';
      case 'rainbow': return '🌈';
      case 'brave': return '🦁';
      case 'phantom': return '👻';
      default: return '👛';
    }
  };

  const getNetworkColor = (networkName: string) => {
    switch (networkName.toLowerCase()) {
      case 'ethereum': return 'bg-blue-500';
      case 'polygon': return 'bg-purple-500';
      case 'bsc': return 'bg-yellow-500';
      case 'arbitrum': return 'bg-cyan-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white py-2 px-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">{getProviderIcon(walletInfo.provider)}</span>
            <span className="font-medium">钱包已连接</span>
          </div>
          
          <div className="hidden sm:flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <span className="opacity-80">地址:</span>
              <span className="font-mono">{walletManager.getShortAddress()}</span>
            </div>
            
            <div className="flex items-center gap-1">
              <span className="opacity-80">网络:</span>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getNetworkColor(walletInfo.networkName)}`}>
                {walletInfo.networkName}
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <span className="opacity-80">状态:</span>
              <span className="text-green-200">{walletManager.getConnectionStatus()}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <i className={`fas fa-chevron-${showDetails ? 'up' : 'down'} text-sm`}></i>
          </button>
          
          <button
            onClick={handleDisconnect}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
          >
            断开连接
          </button>
        </div>
      </div>

      {/* 璇︾粏淇℃伅闈㈡澘 */}
      {showDetails && (
        <div className="mt-3 pt-3 border-t border-white/20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="opacity-80">完整地址:</span>
              <div className="font-mono text-xs mt-1 break-all">
                {walletInfo.address}
              </div>
            </div>
            
            <div>
              <span className="opacity-80">链ID:</span>
              <div className="font-mono text-xs mt-1">
                {walletInfo.chainId}
              </div>
            </div>
            
            <div>
              <span className="opacity-80">连接时间:</span>
              <div className="text-xs mt-1">
                {new Date(walletInfo.connectedAt).toLocaleString()}
              </div>
            </div>
            
            <div>
              <span className="opacity-80">钱包类型:</span>
              <div className="text-xs mt-1 capitalize">
                {walletInfo.provider}
              </div>
            </div>
          </div>
          
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => navigator.clipboard.writeText(walletInfo.address)}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
            >
              <i className="fas fa-copy mr-1"></i>
              复制地址
            </button>
            
            <button
              onClick={() => window.open(`https://etherscan.io/address/${walletInfo.address}`, '_blank')}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
            >
              <i className="fas fa-external-link-alt mr-1"></i>
              区块浏览器
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletStatusBar;

